// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { Credential } from '@daml.js/dtc-credential/lib/Dtc/Credential/V3/Credential';
import { AllowList } from '@daml.js/dtc-registry/lib/Dtc/Registry/V3/Rbac/AllowList';
import { ContractId } from '@daml/types';
import { useLedgerApiClient } from '../../../../contexts/LedgerApiContext';
import { useParties } from '../../../../contexts/PartiesContext';
import { useParticipantServices } from '../../../queries/registry/onboarding/useParticipantServices';

type RemoveWalletsFromAllowListArgs = {
  allowListCid: ContractId<AllowList>;
  credentialCids: ContractId<Credential>[];
};

export const useParticipantRemoveWalletsFromAllowList: () => UseMutationResult<
  void,
  string,
  RemoveWalletsFromAllowListArgs
> = () => {
  const ledgerApi = useLedgerApiClient();
  const participantServices = useParticipantServices();
  const { party } = useParties();

  return useMutation({
    mutationKey: ['participantRemoveWalletsFromAllowList', ledgerApi],
    mutationFn: async ({ allowListCid, credentialCids }) => {
      const participantService = participantServices.data?.find(
        (service) => service.payload.participant === party,
      );
      if (!participantService) {
        throw new Error('Participant service not available');
      }
      return ledgerApi.participantRemoveWalletsFromAllowList(
        participantService.contractId,
        allowListCid,
        credentialCids,
      );
    },
    retry: false,
  });
};
